import { useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { data, portal, setCsrf, type Membership } from "@aawaz/api-client";
import { t } from "@aawaz/i18n";
import { Button, ChannelLogo, ErrorBanner, Field, Icon, Input, Logo, Notice, type IconName } from "@aawaz/ui";
import { useSession } from "../session";
import { renderSVG } from "uqr";

const MEMBERSHIPS_KEY = "aawaz.memberships";

/** Phone OTP (WhatsApp auth template; SMS fallback after 30 s or on request), §11A.1. */
export function LoginPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const qc = useQueryClient();
  const [num, setNum] = useState("");
  // +91 is shown as a fixed prefix; a number typed with its own + code is used as typed
  const phone = num.trim().startsWith("+") ? num.replace(/[^\d+]/g, "") : `+91${num.replace(/\D/g, "")}`;
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"phone" | "code">("phone");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [canSms, setCanSms] = useState(false);
  // Dev test mode only: the server returns the code so no message has to arrive.
  const [devCode, setDevCode] = useState<string | null>(null);

  async function request(channel: "whatsapp" | "sms") {
    setBusy(true);
    setError(null);
    try {
      const out = await data<{ dev_code?: string }>(portal.POST("/portal/v1/auth/otp:request", { body: { phone, channel } }));
      setDevCode(out?.dev_code ?? null);
      if (out?.dev_code) setCode(out.dev_code);
      setStep("code");
      setCanSms(false);
      if (channel === "whatsapp") setTimeout(() => setCanSms(true), 30_000);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    setBusy(true);
    setError(null);
    try {
      const out = await data<{ csrf_token: string; active_tenant_id: string | null; memberships: Membership[] }>(
        portal.POST("/portal/v1/auth/otp:verify", { body: { phone, code } }));
      setCsrf(out.csrf_token);
      sessionStorage.setItem(MEMBERSHIPS_KEY, JSON.stringify(out.memberships));
      await qc.invalidateQueries();
      nav(out.active_tenant_id ? ((loc.state as { from?: string } | null)?.from ?? "/") : "/switch", { replace: true });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title={step === "phone" ? "Sign in to your firm" : "Enter your code"}
                subtitle={step === "phone" ? "We'll send a 4-digit code to your WhatsApp. No password needed." : `Sent to ${phone} on WhatsApp. It expires in 5 minutes.`}>
        <ErrorBanner error={error} />
        {step === "phone" ? (
          <form onSubmit={(e) => { e.preventDefault(); void request("whatsapp"); }}>
            <Field label={t("login.phone")} hint="The WhatsApp number your firm registered for you.">
              {(id) => (
                <div className="ui-phone">
                  <span className="ui-phone-cc">+91</span>
                  <input id={id} className="ui-input ui-input-lg" type="tel" inputMode="tel" autoComplete="tel-national" placeholder="98765 43210"
                         value={num} onChange={(e) => setNum(e.target.value)} required autoFocus />
                </div>
              )}
            </Field>
            <Button variant="primary" className="ui-btn-block" type="submit" busy={busy}>{t("login.send")}</Button>
          </form>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); void verify(); }}>
            {devCode ? (
              <Notice>Test mode: your code is <strong>{devCode}</strong>. It is filled in below.</Notice>
            ) : (
              <Notice>{t("login.sent")}</Notice>
            )}
            <Field label={t("login.code")}>
              {(id) => <Input id={id} className="ui-input-lg ui-otp" inputMode="numeric" autoComplete="one-time-code" pattern="\d{4}"
                              maxLength={4} value={code} onChange={(e) => setCode(e.target.value)} required autoFocus />}
            </Field>
            <Button variant="primary" className="ui-btn-block" type="submit" busy={busy}>{t("login.verify")}</Button>
            <div className="ui-auth-alt" style={{ marginTop: 8 }}>
              <Button type="button" variant="ghost" onClick={() => setStep("phone")}>Change number</Button>
              <Button type="button" variant="ghost" disabled={!canSms || busy} onClick={() => void request("sms")}>{t("login.sms")}</Button>
            </div>
          </form>
        )}
    </AuthLayout>
  );
}

/** Owners must complete TOTP once write-back / Tally write is enabled (SIM-swap mitigation). */
export function TotpPage() {
  const s = useSession();
  const [secret, setSecret] = useState<{ secret: string; otpauth_uri: string } | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  return (
    <AuthLayout title={t("login.totp")} subtitle="Your firm writes files back to its drive, so owners also confirm with an authenticator app.">
        <ErrorBanner error={error} />
        {secret ? (
          <Notice>
            {t("login.totp_enrol")}
            <div className="ui-qr" dangerouslySetInnerHTML={{ __html: renderSVG(secret.otpauth_uri, { border: 1 }) }} />
            <div className="ui-mono" style={{ overflowWrap: "anywhere" }}>{secret.secret}</div>
          </Notice>
        ) : (
          <Button className="ui-btn-block" onClick={async () => {
            try { setSecret(await data(portal.POST("/portal/v1/auth/totp:enroll"))); } catch (e) { setError(e); }
          }}><Icon name="lock" size={16} />First time? Set up authenticator</Button>
        )}
        <form onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            await portal.POST("/portal/v1/auth/totp:verify", { body: { code } });
            await s.refresh();
          } catch (err) { setError(err); } finally { setBusy(false); }
        }}>
          <Field label={t("login.totp")}>
            {(id) => <Input id={id} className="ui-input-lg ui-otp" inputMode="numeric" maxLength={6} value={code}
                            onChange={(e) => setCode(e.target.value)} />}
          </Field>
          <Button variant="primary" className="ui-btn-block" type="submit" busy={busy}>{t("login.verify")}</Button>
        </form>
        <p className="ui-hint" style={{ textAlign: "center", margin: 0 }}>
          Lost the phone with your authenticator app? Write to support@aawazai.com and we reset it after checking it is you.
        </p>
        <Button variant="ghost" className="ui-btn-block" onClick={() => void s.logout()}>Sign out</Button>
    </AuthLayout>
  );
}

export function TenantPicker() {
  const s = useSession();
  const nav = useNavigate();
  const [error, setError] = useState<unknown>(null);
  const stored = JSON.parse(sessionStorage.getItem(MEMBERSHIPS_KEY) ?? "[]") as Membership[];
  const list = s.me?.memberships ?? stored;
  return (
    <AuthLayout title="Choose a firm" subtitle="You're a member of more than one firm.">
        <ErrorBanner error={error} />
        {list.length === 0 ? <p>No firms. <a href="/login">Sign in again</a></p> : null}
        <div style={{ display: "grid", gap: 8 }}>
          {list.map((m) => (
            <button type="button" className="ui-choice" key={m.tenant_id} onClick={async () => {
              try {
                await portal.POST("/portal/v1/auth/tenant", { body: { tenant_id: m.tenant_id } });
                await s.refresh();
                nav("/", { replace: true });
              } catch (e) { setError(e); }
            }}><span><strong>{m.name}</strong><span className="ui-hint" style={{ display: "block" }}>{m.role}</span></span>
              <Icon name="chevron" size={18} /></button>
          ))}
        </div>
    </AuthLayout>
  );
}

const POINTS: { icon: IconName; title: string; text: string }[] = [
  { icon: "message", title: "Collected", text: "Bills sent on WhatsApp or email are read the moment they arrive." },
  { icon: "folder", title: "Filed", text: "By client, financial year, month and type, in your own drive." },
  { icon: "book", title: "In the books", text: "Two-way sync with Zoho Books and Tally; approved bills post back." },
  { icon: "receipt", title: "GST ready", text: "GSTR-2B matched against the bills you hold, so ITC at risk shows up early." },
];

/** Split sign-in screen: brand panel on the left, the form on the right (form only on phones). */
function AuthLayout({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <div className="ui-signin">
      <aside className="ui-signin-side">
        <Logo product="Connect" size={36} onDark />
        <div className="ui-signin-pitch">
          <span className="ui-signin-eyebrow">For CA practices and finance teams</span>
          <h2>Every bill read, filed and in the books before you ask for it.</h2>
          <div className="ui-signin-preview" aria-hidden="true">
            <div className="ui-signin-msg"><ChannelLogo kind="whatsapp" size={22} /><div><strong>Ramesh Traders</strong><span>sent 3 bills · 10:42</span></div></div>
            <div className="ui-signin-filed">
              <span className="ui-signin-tick"><Icon name="check" size={14} /></span>
              <div><strong>Purchase invoice · ₹18,450</strong><span>Filed to Ramesh Traders / FY 2026-27 / Sep</span></div>
              <span className="ui-signin-chip">ITC matched</span>
            </div>
          </div>
          <ul className="ui-signin-points">
            {POINTS.map((p) => (
              <li key={p.title}><span><Icon name={p.icon} size={18} /></span><div><strong>{p.title}</strong>{p.text}</div></li>
            ))}
          </ul>
        </div>
        <div className="ui-signin-foot"><Icon name="lock" size={14} />Encrypted at rest<span>·</span>Every access logged<span>·</span>Data kept in India</div>
      </aside>
      <main className="ui-signin-main">
        <div className="ui-signin-card">
          <div className="ui-signin-mlogo"><Logo product="Connect" size={36} /></div>
          <h1>{title}</h1>
          {subtitle ? <p className="ui-signin-sub">{subtitle}</p> : null}
          {children}
        </div>
        <p className="ui-signin-legal">Need access? Ask your firm's owner to add your WhatsApp number.</p>
      </main>
    </div>
  );
}
